import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import moment from "moment";
import { Stepper } from "components/Stepper";
import { Timer } from "components/Timer";
import { Link } from "react-router-dom";
import { Tooltip } from "primereact/tooltip";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Badge } from "primereact/badge";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import DownloadCharts from "./DownloadCharts";
import LiveIcon from "assets/icons/live.png";
import FemaleIcon from "assets/icons/FemaleAvatar.png";
import MaleIcon from "assets/icons/MaleAvatar.png";
import NoProfile from "assets/icons/NoProfile.png";
import ExportToCsv from "./ExportDataIntoExcel";
import { useTranslation } from "react-i18next";
const alphabets = [...Array(26)].map((_, i) => String.fromCharCode(65 + i));

export default function Stages(props) {
  const {
    data = {},
    toggleFullScreen,
    isJourneyTableFullScreen = false,
    isFullScreen = false,
    getDRDTooltip,
    legendValue = 0,
    divId,
    showLive,
    isPatientFilterApplied,
    moduleData,
  } = props;
  const { t } = useTranslation();
  const FULL_SCREEN = t("FULL_SCREEN");
  const EXIT = t("EXIT");
  const INVESTIGATION = t("INVESTIGATION");
  const JOURNEY_STARTED_ON = t("JOURNEY_STARTED_ON");
  const DRAG_AND_DROP = t("DRAG_AND_DROP");
  const journeyData = data.rowData || [];
  const tableHeadData = data.tableHeadData || [];
  const [currentPage, setCurrentPage] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const itemsPerPage = 7; // Number of items to display per page
  const totalPages = Math.ceil(journeyData.length / itemsPerPage);
  const [currentTrainIndex, setCurrentTrainIndex] = useState(0);
  const divRef = useRef(null);
  const [dynamicWidth, setDynamicWidth] = useState(0);
  const [addClass, setAddClass] = useState(false);
  useEffect(() => {
    // Function to get the width of the div element
    const intialWidth = divRef.current ? divRef.current.scrollWidth : 100;
    const getWidth = () => {
      // Check if the ref is not null
      if (divRef.current) {
        // Get the bounding client rectangle of the div element
        const width = divRef.current.scrollWidth;
        setDynamicWidth(width);
      }
    };
    setDynamicWidth(intialWidth);
    window.addEventListener("resize", getWidth);
    return () => {
      window.removeEventListener("resize", getWidth);
    };
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused && journeyData.length > itemsPerPage) {
        intervalPageChange();
      }
    }, 5000); // Adjust the interval duration as needed (in milliseconds)
    return () => clearInterval(interval);
  }, [journeyData, itemsPerPage, totalPages, isPaused]);

  const intervalPageChange = () => {
    setCurrentPage((prevPage) => {
      return prevPage + 1 === totalPages
        ? prevPage + 1
        : (prevPage + 1) % totalPages;
    });
  };

  const handleMouseEnter = () => setIsPaused(true);

  const handleMouseLeave = () => setIsPaused(false);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    setAddClass(true);
    const timeout = setTimeout(() => {
      setAddClass(false);
    }, 3000);
    return () => clearTimeout(timeout);
  }, [currentPage]);

  const renderPagination = () => {
    return Array.from({ length: totalPages }, (_, index) => (
      <a
        href="#"
        key={index}
        className={
          currentPage === index + 1
            ? "pagination__numbers active"
            : "pagination__numbers"
        }
        onClick={() => paginate(index + 1)}
      >
        {index + 1}
      </a>
    ));
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const tableAccordion = (data) => {
    return (
      <Accordion activeIndex={0}>
        {Object.keys(data).map((d, index) => (
          <AccordionTab
            key={index}
            header={
              <span className="flex align-items-center gap-2 w-full">
                <span className="font-bold white-space-nowrap">
                  {d} - {isPatientFilterApplied}
                </span>
                <Badge value={data[d].length} className="ml-auto" />
              </span>
            }
          >
            <p className="m-0">{renderContent(data[d])}</p>
          </AccordionTab>
        ))}
      </Accordion>
    );
  };
  const renderTableData = (data) =>
    data.slice(startIndex, endIndex).map((patient, index) => {
      const {
        imageSource,
        name = "",
        number = "",
        steps = [],
        isLate = false,
        timeString = "",
        lastStep = "",
        radiologist = "",
      } = patient;
      return (
        <div className="mt-1" key={index}>
          <Stepper
            steps={steps}
            isLate={isLate}
            lastStep={lastStep}
            dynamicWidth={dynamicWidth}
            mainIndex={index}
            alphabets={alphabets}
            legendValue={legendValue}
            addClass={addClass}
            patientData={{
              module_id: moduleData.id,
              ...patient,
              org_code: moduleData.org_code,
            }}
          />
        </div>
      );
    });

  const renderContent = (data) => {
    return (
      <div className="flex" style={{ gap: "20px" }}>
        <div
          className="flex flex-column justify-content-center"
          style={{ gap: "5px" }}
        >
          {(tableHeadData || []).length ? (
            <React.Fragment>
              <div
                className="text-lg font-bold"
                style={{ gap: "10px", width: "300px" }}
              >
                {tableHeadData[0].label}
              </div>
            </React.Fragment>
          ) : (
            <></>
          )}
          {data.slice(startIndex, endIndex).map((patient, index) => {
            const {
              imageSource,
              name = "",
              number = "",
              steps = [],
              isLate = false,
              timeString = "",
              lastStep = "",
              radiologist = "",
              investigation = "",
              patient_type = "",
              gender = "",
            } = patient;
            const journetStartDate =
              steps.find((step) => Object.values(step)[0] !== "") || {};
            let profileIcon = NoProfile;
            if (gender === "male") {
              profileIcon = MaleIcon;
            } else if (gender === "female") {
              profileIcon = FemaleIcon;
            }
            return (
              <div
                className="flex align-items-center p-1 mt-1 relative card"
                style={{
                  gap: "8px",
                  background: "inherit",
                  borderRadius: "4px",
                }}
                key={index}
              >
                <div
                  style={{
                    border: `2px solid ${isLate ? "#D20000" : "#2fe90f"}`,
                    height: "60px",
                  }}
                />
                {patient_type ? (
                  <Tag
                    value={patient_type}
                    className="absolute"
                    style={{
                      top: "-10px",
                      left: "15px",
                      paddingRight: "6px",
                      paddingLeft: "6px",
                    }}
                  />
                ) : null}
                <div
                  style={{ width: 35, height: 34, borderRadius: 50 }}
                  className="flex justify-content-center align-items-center text-center"
                >
                  <Link to={`/patient/${number}`}>
                    <img
                      src={profileIcon}
                      style={{ width: 35, height: 34, borderRadius: 50 }}
                    />
                  </Link>
                </div>
                <Tooltip target=".investigation-tooltip" />
                <Tooltip target=".journey-start" />
                <div
                  className="flex flex-row align-items-center"
                  style={{ paddingRight: "10px", gap: "8px" }}
                >
                  <div
                    className="flex flex-column justify-content-between w-full gap-1"
                    style={{ gap: "4px" }}
                  >
                    <div className="text-700 text-sm flex justify-content-between">
                      <div className="text-sm font-bold flex">
                        <Link
                          to={`/patient/${number}`}
                          className="!text-[var(--primary-color)]"
                        >
                          {name}
                        </Link>
                      </div>
                      <div
                        className="investigation-tooltip"
                        data-pr-tooltip={INVESTIGATION}
                      >
                        {investigation}
                      </div>
                    </div>
                    <div className="text-sm font-bold flex">
                      <div style={{ width: "50px" }}>{number}</div>
                      <Divider layout="vertical" className="p-0" />
                      <div
                        className="overflow-hidden	white-space-nowrap text-overflow-ellipsis radiologist-tooltip"
                        data-pr-tooltip={radiologist}
                        style={{ width: "75px" }}
                      >
                        {radiologist}
                      </div>
                      <Divider layout="vertical" className="p-0" />
                      <div
                        className="overflow-hidden	white-space-nowrap text-overflow-ellipsis journey-start"
                        data-pr-tooltip={JOURNEY_STARTED_ON}
                        style={{ width: "75px" }}
                      >
                        {journetStartDate.date}
                      </div>
                      <Tooltip target={`.radiologist-tooltip`} />
                      <Divider layout="vertical" className="p-0" />
                      <Timer
                        timeString={timeString}
                        isLate={isLate}
                        lastStep={lastStep}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div
          className="flex flex-column overflow-scroll w-full justify-content-center hide-scroll"
          style={{ gap: "10px" }}
        >
          <div
            className="flex flex-nowrap w-full"
            ref={divRef}
            style={{ gap: "26px" }}
          >
            {/* {(tableHeadData || []).slice(1).map((header, index) => (
          <div key={header.key} className="text-lg font-bold w-full">
            {header.label}
          </div>
        ))} */}
          </div>
          {renderTableData(data)}
        </div>
      </div>
    );
  };

  const formatExcelData = (data) =>
    data.map((f) =>
      Object.keys(f).reduce((previous, d) => {
        let s = "";
        if (Array.isArray(f[d])) {
          s = f[d].join(" ");
        } else {
          s = f[d];
        }
        return {
          ...previous,
          [d]: s,
        };
      }, {})
    );

  return (
    <main
      id="modules"
      className={`main-page ${
        isJourneyTableFullScreen ? "bg-inherit p-3" : ""
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex justify-content-between">
        <div className="flex gap-3 align-items-center mb-2">
          {!isJourneyTableFullScreen &&
            getDRDTooltip("drd-scan-journey", DRAG_AND_DROP)}
          <div>
            <Tooltip target=".journey-table-full-tip" />
            <i
              className={`journey-table-full-tip pi ${
                isJourneyTableFullScreen
                  ? "pi-window-minimize"
                  : "pi-window-maximize"
              } cursor-pointer`}
              data-pr-tooltip={isJourneyTableFullScreen ? EXIT : FULL_SCREEN}
              data-pr-position="left"
              onClick={() => {
                toggleFullScreen("table");
              }}
            />
          </div>
          <DownloadCharts divId={divId} filename={moduleData.module_name} />
          <ExportToCsv
            jsonData={formatExcelData(data.excelData)}
            filename={`${moduleData.module_name} ${moment().format(
              "YYYY-MM-DD HH-mm-ss"
            )}.csv`}
          />
        </div>
        {showLive === "connected" ? (
          <img
            src={LiveIcon}
            className="animate-pulse"
            style={{ width: "4rem" }}
          />
        ) : (
          ""
        )}
      </div>
      <section className="page-section q-pa-md">
        <div className="container-fluid">
          {!journeyData.length ? (
            <Message
              severity="error"
              text="No data found!"
              className="w-full"
            />
          ) : isPatientFilterApplied ? (
            tableAccordion(_.groupBy(journeyData, "encounterNumber"))
          ) : (
            renderContent(journeyData)
          )}
          {journeyData.length ? (
            <>
              <div className="m-3 flex align-items-center justify-content-center">
                {totalPages > 1 && (
                  <>
                    <span>
                      {t("PAGE")} {currentPage} {t("OF")} {totalPages}
                    </span>
                    <div className="pagination">
                      <a
                        href="#"
                        onClick={() =>
                          currentPage > 1 && setCurrentPage(currentPage - 1)
                        }
                      >
                        &laquo;
                      </a>
                      {renderPagination()}
                      <a
                        href="#"
                        onClick={() =>
                          currentPage !== totalPages &&
                          setCurrentPage(currentPage + 1)
                        }
                      >
                        &raquo;
                      </a>
                    </div>
                  </>
                )}
              </div>
              {legendValue ? (
                <div
                  className="border w-full h-full flex p-3 flex-wrap"
                  style={{ borderRadius: "4px", gap: "8px" }}
                >
                  {(tableHeadData || []).slice(1).map((header, index) => (
                    <div
                      key={header.key}
                      className="text-xx font-bold w-full"
                      style={{
                        flex: "0 0 calc(33.33% - 10px)",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                      }}
                    >
                      {alphabets[index]} - {header.label}
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </section>
    </main>
  );
}
